import { Group } from './types';

//MUI Icons
import EditNoteIcon from '@mui/icons-material/EditNote';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import InsertPageBreakIcon from '@mui/icons-material/InsertPageBreak';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import HtmlIcon from '@mui/icons-material/Html';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import StyleIcon from '@mui/icons-material/Style';
import { t } from 'i18next';

const GROUPS: Group[] = [
  {
    name: 'insert',
    title: 'Insert',
    commands: [
      {
        name: 'unorderedList',
        label: 'Bulleted List',
        icon: FormatListBulletedIcon,
        description: 'Bulleted list of items',
        aliases: ['ul'],
        action: (editor) => {
          editor.chain().focus().toggleBulletList().run();
        },
      },
      {
        name: 'numberedList',
        label: 'Numbered List',
        icon: FormatListNumberedIcon,
        description: 'Ordered list of items',
        aliases: ['ol'],
        action: (editor) => {
          editor.chain().focus().toggleOrderedList().run();
        },
      },
      {
        name: 'quote',
        label: 'Quote',
        icon: FormatQuoteIcon,
        description: 'Element for quoting',
        aliases: ['ul'],
        action: (editor) => {
          editor.chain().focus().setBlockquote().run();
        },
      },
      {
        name: 'horizontalRule',
        label: 'Horizontal Rule',
        icon: HorizontalRuleIcon,
        description: 'Insert a horizontal rule',
        aliases: ['hr'],
        action: (editor) => {
          editor.chain().focus().setHorizontalRule().run();
        },
      },
      {
        name: 'pageBreak',
        label: 'Page Break',
        icon: InsertPageBreakIcon,
        description: 'Insert a page break',
        aliases: ['hr'],
        action: (editor) => {
          editor.chain().focus().setPageBreak().run();
        },
      },
      {
        name: 'inlineNote',
        label: 'Inline Note',
        icon: EditNoteIcon,
        description: 'Insert an inline note',
        aliases: ['ins'],
        action: (editor) => {
          editor.chain().focus().setInlineNote().run();
        },
      },
      {
        name: 'table',
        label: 'Table',
        icon: TableChartOutlinedIcon,
        description: 'Insert a table',
        aliases: ['table'],
        action: (editor) => {
          editor?.chain().focus().insertTable().run();
        },
      },
    ],
  },
  {
    name: 'assets',
    title: 'Assets',
    commands: [
      {
        name: 'image',
        label: 'Image',
        icon: ImageIcon,
        iconColor: 'image',
        description: 'Insert an image asset',
        aliases: ['asset-img', 'img'],
        action: (editor) => {
          editor.chain().focus().insertBloxInline({ type: 'image' }).run();
        },
      },
      {
        name: 'video',
        label: 'Video',
        icon: VideocamOutlinedIcon,
        iconColor: 'video',
        description: 'Insert a video asset',
        aliases: ['asset-video', 'video'],
        action: (editor) => {
          editor.chain().focus().insertBloxInline({ type: 'video' }).run();
        },
      },
      {
        name: 'audio',
        label: 'Audio',
        icon: VolumeUpIcon,
        iconColor: 'audio',
        description: 'Insert an audio asset',
        aliases: ['asset-audio'],
        action: (editor) => {
          editor.chain().focus().insertBloxInline({ type: 'audio' }).run();
        },
      },
      {
        name: 'link',
        label: 'Link',
        icon: LinkIcon,
        iconColor: 'aLink',
        description: 'Insert a link asset',
        aliases: ['asset-link', 'link'],
        action: (editor) => {
          editor.chain().focus().insertBloxInline({ type: 'link' }).run();
        },
      },
      {
        name: 'html',
        label: 'HTML',
        icon: HtmlIcon,
        iconColor: 'html',
        description: 'Insert an HTML asset',
        aliases: ['asset-html', 'html'],
        action: (editor) => {
          editor.chain().focus().insertBloxInline({ type: 'html' }).run();
        },
      },
      {
        name: 'file',
        label: 'File',
        icon: AttachFileOutlinedIcon,
        iconColor: 'file',
        description: 'Insert a file asset',
        aliases: ['asset-file', 'file'],
        action: (editor) => {
          editor.chain().focus().insertBloxInline({ type: 'file' }).run();
        },
      },
      {
        name: 'pdf',
        label: 'PDF',
        icon: PictureAsPdfIcon,
        iconColor: 'pdf',
        description: 'Insert a PDF asset',
        aliases: ['asset-pdf', 'pdf'],
        action: (editor) => {
          editor.chain().focus().insertBloxInline({ type: 'pdf' }).run();
        },
      },
    ],
  },
  {
    name: 'format',
    title: 'Format',
    commands: [
      {
        name: 'bold',
        label: 'Bold',
        icon: FormatBoldIcon,
        description: 'Bold text',
        aliases: ['b'],
        action: (editor) => {
          editor.chain().focus().toggleBold().run();
        },
      },
      {
        name: 'italic',
        label: 'Italic',
        icon: FormatItalicIcon,
        description: 'Italicize text',
        aliases: ['i'],
        action: (editor) => {
          editor.chain().focus().toggleItalic().run();
        },
      },
      {
        name: 'underline',
        label: 'Underline',
        icon: FormatUnderlinedIcon,
        description: 'Underline text',
        aliases: ['u'],
        action: (editor) => {
          editor.chain().focus().toggleUnderline().run();
        },
      },
    ],
  },
  {
    name: 'relcontent',
    title: t('blox.editorial.asset.related_content.label'),
    commands: [
      {
        name: 'relcontent',
        label: t('blox.editorial.asset.related_content.label'),
        icon: StyleIcon,
        iconColor: 'relcontent',
        description: t('blox.editorial.asset.related_content.actions.insert.label'),
        aliases: ['related-content', 'relcontent'],
        action: (editor) => {
          editor.chain().focus().insertBloxInline({ type: 'relcontent' }).run();
        },
      },
    ],
  },
];

export default GROUPS;
